import React, { useEffect, useState, useRef } from 'react';
import { FolderOpen, UploadCloud, Send, Trash2, Loader2, FileText, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const GROUPS = [['g5', 'Grade 5'], ['g67', 'Grades 6-7'], ['g8', 'Grade 8']];
const fmtSize = (b) => (!b ? '' : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const TeacherVault = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const inputRef = useRef(null);

  const load = () => api.get('/academy/vault', { headers: authHeaders() }).then((r) => setFiles(r.data.files || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/academy/vault/upload', fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success(`Added "${file.name}" to your vault`);
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const del = async (f) => {
    if (!window.confirm(`Remove "${f.original_filename}" from the vault?`)) return;
    await api.delete(`/academy/vault/${f.id}`, { headers: authHeaders() }).then(() => { toast.success('Removed'); load(); }).catch(() => toast.error('Failed'));
  };

  return (
    <div data-testid="teacher-vault">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><FolderOpen className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black uppercase tracking-[0.2em]">Document Vault <span className="text-emerald-400">({files.length})</span></h3></div>
        <button onClick={() => inputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-700 text-black px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] transition-colors" data-testid="vault-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload New
        </button>
        <input ref={inputRef} type="file" className="hidden" onChange={onUpload} data-testid="vault-file-input" />
      </div>
      <p className="text-xs text-zinc-500 mb-3">Upload a file once, then send it to any scholar. Reuse anything already in the vault — no need to re-upload.</p>
      {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
        files.length === 0 ? <div className="text-xs text-zinc-600 border-2 border-dashed border-zinc-800 py-6 text-center" data-testid="vault-empty">Your vault is empty — upload a handout, slide deck, or worksheet to get started.</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {files.map((f) => (
              <div key={f.id} className="border-2 border-zinc-800 bg-black/40 p-3 flex flex-col" data-testid={`vault-file-${f.id}`}>
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0"><div className="text-xs font-bold text-white truncate">{f.original_filename}</div><div className="text-[10px] text-zinc-600">{fmtSize(f.size)}</div></div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => setSendFor(f)} className="flex-1 flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-black py-1.5 text-[10px] font-black uppercase tracking-widest" data-testid={`vault-send-${f.id}`}><Send className="w-3.5 h-3.5" /> Send</button>
                  <button onClick={() => del(f)} className="text-zinc-500 hover:text-red-400" data-testid={`vault-del-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {sendFor && <SendModal file={sendFor} onClose={() => setSendFor(null)} />}
    </div>
  );
};

const SendModal = ({ file, onClose }) => {
  const [scope, setScope] = useState('all');
  const [gradeGroup, setGradeGroup] = useState('g5');
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState({});
  const [title, setTitle] = useState(file.original_filename);
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (scope === 'student' && students.length === 0) api.get('/academy/vault/students', { headers: authHeaders() }).then((r) => setStudents(r.data.students || [])).catch(() => {}); }, [scope]); // eslint-disable-line

  const send = async () => {
    setBusy(true);
    try {
      const body = { file_id: file.id, title, note, scope, notify_email: notify };
      if (scope === 'grade') body.grade_group = gradeGroup;
      if (scope === 'student') body.student_ids = Object.keys(picked).filter((k) => picked[k]);
      const r = await api.post('/academy/vault/send', body, { headers: authHeaders() });
      toast.success(`Sent to ${r.data.sent} scholar(s)${r.data.emailed ? ' + emailed' : ''}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="vault-send-modal">
      <div className="w-full max-w-lg bg-zinc-950 border-2 border-zinc-800 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-sm font-black uppercase tracking-[0.2em] truncate">Send · {file.original_filename}</h3><button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button></div>
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Title scholars will see</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 mb-3 w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm focus:border-orange-500 outline-none" data-testid="send-title" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. 'Read before Thursday')" className="mb-3 w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm focus:border-orange-500 outline-none" data-testid="send-note" />
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Send to</label>
        <select value={scope} onChange={(e) => setScope(e.target.value)} className="mt-1 w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm focus:border-orange-500 outline-none" data-testid="send-scope">
          <option value="all">All students</option>
          <option value="grade">A grade group</option>
          <option value="student">Specific students</option>
        </select>
        {scope === 'grade' && <select value={gradeGroup} onChange={(e) => setGradeGroup(e.target.value)} className="mt-2 w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm" data-testid="send-grade">{GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}
        {scope === 'student' && (
          <div className="mt-2 max-h-44 overflow-y-auto border-2 border-zinc-800 p-2" data-testid="send-student-list">
            {students.length === 0 ? <div className="text-xs text-zinc-600 p-2">Loading…</div> : students.map((s) => (
              <label key={s.id} className="flex items-center gap-2 py-1 text-xs text-zinc-300"><input type="checkbox" checked={!!picked[s.id]} onChange={(e) => setPicked((p) => ({ ...p, [s.id]: e.target.checked }))} data-testid={`send-pick-${s.id}`} />{s.name} <span className="text-zinc-600">· G{s.grade_level}</span></label>
            ))}
          </div>
        )}
        <label className="mt-3 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="send-notify-label">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="send-notify" /> Also email them a heads-up
        </label>
        <button onClick={send} disabled={busy} className="mt-4 w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em]" data-testid="send-confirm-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Document
        </button>
      </div>
    </div>
  );
};

export default TeacherVault;
